import { api } from "services/restUtil";
import type {
  EventSummary,
  EventType,
  LessonFeedback,
  LessonSessionStatus,
} from "services/sessions/model";
import { getSessionUser } from "services/users/auth";
import { isUserTeacher, getUserFullName } from "services/users/utils";
import { getCurrentServerTime } from "utils/date";
import { fetchSession } from "./lesson";

type FetchFeedbackArgs = {
  id: string;
};

type FetchFeedbackResponse = {
  feedback: LessonFeedback;
};

export function fetchFeedback(args: FetchFeedbackArgs) {
  return Promise.all([
    api.get<FetchFeedbackResponse>(`/session/lesson/${args.id}/feedback`),
    fetchSession(args),
    getHomework({ lesson_id: args.id }),
  ]).then(([feedbackResp, session, homeworkResp]) => ({
    ...session,
    feedback: feedbackResp.feedback,
    homework: homeworkResp.homework,
  }));
}

type PatchFeedbackArgs = {
  id: string;
};

type PatchFeedbackPayload = LessonFeedback;

export function patchFeedback(args: PatchFeedbackArgs, payload: PatchFeedbackPayload) {
  return api.patch<PatchFeedbackPayload>(`/session/lesson/${args.id}/feedback`, {
    grammar: payload.grammar,
    homework: payload.homework,
    other: payload.other,
    practice: payload.practice,
    summary: payload.summary,
    vocabulary: payload.vocabulary,
  });
}

type LessonResourceArgs = {
  lesson_id: string;
};

type GetWorkspaceResponse = {
  workspace: string;
};

export function getWorkspace(args: LessonResourceArgs) {
  return api.get<GetWorkspaceResponse>(`/session/lesson/${args.lesson_id}/workspace`);
}

type SaveWorkspaceArgs = LessonResourceArgs & GetWorkspaceResponse;

export function saveWorkspace(args: SaveWorkspaceArgs) {
  return api.patch<GetWorkspaceResponse>(`/session/lesson/${args.lesson_id}/workspace`, {
    workspace: args.workspace,
  });
}

type GetHomeworkResponse = {
  homework: string;
};

export function getHomework(args: LessonResourceArgs) {
  return api.get<GetHomeworkResponse>(`/session/lesson/${args.lesson_id}/homework`);
}

type SaveHomeworkArgs = LessonResourceArgs & GetHomeworkResponse;

export function saveHomework(args: SaveHomeworkArgs) {
  return api.patch<GetHomeworkResponse>(`/session/lesson/${args.lesson_id}/homework`, {
    homework: args.homework,
  });
}

type GetChatResponse = {
  chat: {
    user_id: string;
    text: string;
    timestamp: string;
  }[];
};

export function getChat(args: LessonResourceArgs) {
  return api.get<GetChatResponse>(`/session/lesson/${args.lesson_id}/chat`).then(resp => {
    if (!resp?.chat) {
      throw new Error("Invalid response: shouldn’t be empty and contain an array 'chat'");
    }

    return resp;
  });
}

type GetChatHistoryUser = {
  data: {
    user: {
      first_name?: string | null;
      last_name?: string | null;
    };
    instructor: {
      first_name?: string | null;
    };
  };
};

type GetChatHistoryResponse = {
  chat: Array<{
    text: string;
    user_id: string;
    userName: string | null;
    role: "instructor" | "student";
  }>;
};

export function getChatHistory(args: LessonResourceArgs, user: GetChatHistoryUser) {
  let prevMessageFrom: string | null = null;
  const currentUser = getSessionUser(true);

  return getChat(args).then(resp => {
    const chatMessages: GetChatHistoryResponse["chat"] = [];
    resp.chat.forEach(message => {
      let name: string | null = null;
      if (message.user_id !== prevMessageFrom) {
        if (message.user_id === currentUser.id) {
          name = "You";
        } else if (isUserTeacher()) {
          name = getUserFullName(user.data.user);
        } else {
          name = user.data.instructor.first_name ?? null;
        }
      }

      prevMessageFrom = message.user_id;

      chatMessages.push({
        text: message.text,
        user_id: message.user_id,
        userName: name,
        role: message.user_id === currentUser.id ? "instructor" : "student",
      });
    });

    return { chat: chatMessages };
  });
}

type SaveChatMessageArgs = LessonResourceArgs & {
  text: string;
};

type SaveChatMessagePayload = {
  text: string;
  timestamp: string;
};

export function saveChatMessage(args: SaveChatMessageArgs) {
  const payload: SaveChatMessagePayload = {
    text: args.text,
    timestamp: getCurrentServerTime().toISOString(),
  };

  return api.patch<SaveChatMessagePayload>(
    `/session/lesson/${args.lesson_id}/chat`,
    payload
  );
}

type GetEventsArgs = {
  lessonId: string;
};

type GetEventsResponse = {
  list: EventSummary[];
  total_count: number;
  session: {
    id: string;
    status: LessonSessionStatus;
  };
};

export function getEvents(args: GetEventsArgs) {
  return api
    .get<GetEventsResponse>(`/session/lesson/${args.lessonId}/events`)
    .then(resp => ({
      events: resp.list,
      status: resp.session.status,
    }));
}

type SaveEventArgs = {
  lessonId: string;
  eventType: EventType;
};

type SaveEventPayload = {
  type: EventType;
};

export function saveEvent(args: SaveEventArgs) {
  const payload: SaveEventPayload = {
    type: args.eventType,
  };

  return api.post<SaveEventPayload>(`/session/lesson/${args.lessonId}/events`, payload);
}

type SaveTranscriptionArgs = {
  session_id: string;
  text: string;
};

type SaveTranscriptionPayload = {
  transcription: string;
};

export function saveTranscription(args: SaveTranscriptionArgs) {
  const payload: SaveTranscriptionPayload = {
    transcription: args.text,
  };

  return api.post<SaveTranscriptionPayload>(
    `/session/lesson/${args.session_id}/transcription`,
    payload
  );
}
